// ABOUTME: Unified drawer component that opens from bottom or right with consistent API
import * as React from "react";

import { cn } from "@/shared/lib/css";
import {
  Drawer as VaulDrawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from "@/shared/ui/kit/drawer";

import { DIALOG_TOKENS } from "./dialog-tokens";

// ============================================================================
// Types
// ============================================================================

export interface UnifiedDrawerProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  direction?: "bottom" | "right";
  modal?: boolean;
  children: React.ReactNode;
}

export interface UnifiedDrawerContentProps {
  className?: string;
  children: React.ReactNode;
}

export interface UnifiedDrawerHeaderProps {
  className?: string;
  children: React.ReactNode;
}

export interface UnifiedDrawerFooterProps {
  className?: string;
  children: React.ReactNode;
}

export interface UnifiedDrawerTitleProps {
  className?: string;
  children: React.ReactNode;
}

export interface UnifiedDrawerDescriptionProps {
  className?: string;
  children: React.ReactNode;
}

export interface UnifiedDrawerBodyProps {
  className?: string;
  children: React.ReactNode;
}

export interface UnifiedDrawerTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
}

export interface UnifiedDrawerCloseProps {
  children: React.ReactNode;
  className?: string;
}

// ============================================================================
// Context
// ============================================================================

interface UnifiedDrawerContextValue {
  direction: "bottom" | "right";
}

const UnifiedDrawerContext = React.createContext<UnifiedDrawerContextValue>({
  direction: "bottom",
});

function useUnifiedDrawerContext() {
  return React.useContext(UnifiedDrawerContext);
}

// ============================================================================
// Components
// ============================================================================

function UnifiedDrawerRoot({
  open,
  onOpenChange,
  direction = "bottom",
  modal = true,
  children,
}: UnifiedDrawerProps) {
  return (
    <UnifiedDrawerContext.Provider value={{ direction }}>
      <VaulDrawer
        open={open}
        onOpenChange={onOpenChange}
        direction={direction}
        modal={modal}
      >
        {children}
      </VaulDrawer>
    </UnifiedDrawerContext.Provider>
  );
}

function UnifiedDrawerContent({
  className,
  children,
}: UnifiedDrawerContentProps) {
  const { direction } = useUnifiedDrawerContext();

  return (
    <DrawerContent
      className={cn(
        "z-50",
        direction === "bottom" && "max-h-[90dvh]",
        direction === "right" && "h-full",
        "flex flex-col",
        className,
      )}
    >
      {children}
    </DrawerContent>
  );
}

function UnifiedDrawerHeader({
  className,
  children,
}: UnifiedDrawerHeaderProps) {
  return (
    <div
      className={cn(
        DIALOG_TOKENS.headerPadding,
        DIALOG_TOKENS.headerBorder,
        className,
      )}
    >
      {children}
    </div>
  );
}

function UnifiedDrawerFooter({
  className,
  children,
}: UnifiedDrawerFooterProps) {
  return (
    <div className={cn(DIALOG_TOKENS.footerPadding, className)}>{children}</div>
  );
}

function UnifiedDrawerTitle({ className, children }: UnifiedDrawerTitleProps) {
  return (
    <DrawerTitle className={cn(DIALOG_TOKENS.titleSize, className)}>
      {children}
    </DrawerTitle>
  );
}

function UnifiedDrawerDescription({
  className,
  children,
}: UnifiedDrawerDescriptionProps) {
  return (
    <DrawerDescription
      className={cn("text-sm text-muted-foreground", className)}
    >
      {children}
    </DrawerDescription>
  );
}

export interface UnifiedDrawerBodyProps
  extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  children: React.ReactNode;
}

const UnifiedDrawerBody = React.forwardRef<
  HTMLDivElement,
  UnifiedDrawerBodyProps
>(({ className, children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        DIALOG_TOKENS.contentScrollable,
        DIALOG_TOKENS.contentPadding,
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
});
UnifiedDrawerBody.displayName = "UnifiedDrawerBody";

function UnifiedDrawerTrigger({
  children,
  asChild,
}: UnifiedDrawerTriggerProps) {
  return <DrawerTrigger asChild={asChild}>{children}</DrawerTrigger>;
}

function UnifiedDrawerClose({ children, className }: UnifiedDrawerCloseProps) {
  return <DrawerClose className={className}>{children}</DrawerClose>;
}

// ============================================================================
// Exports
// ============================================================================

export const UnifiedDrawer = Object.assign(UnifiedDrawerRoot, {
  Content: UnifiedDrawerContent,
  Header: UnifiedDrawerHeader,
  Footer: UnifiedDrawerFooter,
  Body: UnifiedDrawerBody,
  Title: UnifiedDrawerTitle,
  Description: UnifiedDrawerDescription,
  Trigger: UnifiedDrawerTrigger,
  Close: UnifiedDrawerClose,
});

export {
  UnifiedDrawerContent,
  UnifiedDrawerHeader,
  UnifiedDrawerFooter,
  UnifiedDrawerBody,
  UnifiedDrawerTitle,
  UnifiedDrawerDescription,
  UnifiedDrawerTrigger,
  UnifiedDrawerClose,
};

export { useUnifiedDrawerContext as useUnifiedDrawer };
